import PropTypes from 'prop-types';
import cx from 'classnames';
import { Radio, Checkbox, Typography, Switch, Select } from 'antd';
import { RightOutlined } from '@ant-design/icons';

import { CustomIcon } from '$src/components/CustomIcon';
import './index.less';

const { Link } = Typography;
const { Option } = Select;

const types = {
  ingress: {
    preffixIcon: true,
    suffixArrow: true,
  },
  radio: {
    preffixRadio: true,
  },
  'round-cta': {
    preffixIcon: true,
    suffixIcon: true,
  },
  checkbox: {
    suffixCheckbox: true,
  },
  textlink: {
    preffixIcon: true,
    suffixTextLink: true,
  },
  toogle: {
    preffixIcon: true,
    suffixToogle: true,
    showLock: true,
  },
  select: {
    preffixIcon: true,
    suffixSelect: true,
  },
  plain: {
    preffixIcon: true,
  },
  plainNoIcon: {
    preffixIcon: false,
  },
};

function ActionCell({
  value,
  children,
  description,
  disabled,
  iconName,
  onClick,
  onChange,
  suffixIconName,
  textLink,
  type,
  options,
  loading,
  className,
  content,
  hideContentWhenUnChecked,
  disableRootClick,
  isDanger,
  isDescriptionError,
  id,
  hasBlueWarning,
  ...other
}) {
  const typeOptions = types[type];
  return (
    <div
      className={cx('action-cell', `action-cell--${type}`, className, {
        'action-cell--selected': !!value,
        'action-cell--no-pointer': disableRootClick,
      })}
      onClick={!disabled && !disableRootClick ? onClick : undefined}
      {...other}
    >
      <div className="action-cell__row">
        {iconName
          && (typeOptions.preffixIcon && !iconName.endsWith('.svg') ? (
            <img src={iconName} alt="device icon" className="device-icon__image"/>
          ) : (
            <CustomIcon
              type="regular"
              name={iconName}
              className={cx('action-cell__preffix', 'action-cell__icon', {
                'action-cell__icon--danger': isDanger,
              })}
            />
          ))}
        {typeOptions.preffixRadio && <Radio checked={value} className="action-cell__preffix"/>}
        <div className="action-cell__text">
          <div className="action-cell__title">{children}</div>
          <div
            className={cx('action-cell__description', 'action-cell__icon', {
              'action-cell__description--error': isDescriptionError,
              'action-cell__description--onvif': hasBlueWarning && !isDescriptionError,
            })}
          >
            {description}
          </div>
        </div>
        {typeOptions.suffixArrow && <RightOutlined className="action-cell__arrow"/>}
        {typeOptions.suffixIcon && (
          <div className="action-cell__suffix-icon-container">
            <CustomIcon type="regular" name={suffixIconName} className="action-cell__suffix-icon"/>
          </div>
        )}
        {typeOptions.suffixCheckbox && <Checkbox onChange={onChange} checked={value} className="action-cell__checkbox"/>}
        {typeOptions.suffixTextLink && <Link className="action-cell__textlink">{textLink}</Link>}
        {typeOptions.suffixToogle && (
          <Switch className="action-cell__switch" onChange={onChange} checked={value} disabled={disabled} loading={loading} data-testid={id}/>
        )}
        {typeOptions.suffixSelect && (
          <Select className="action-cell__select" value={value} disabled={disabled} onChange={onChange} loading={loading}>
            {options.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        )}
      </div>
      {(!hideContentWhenUnChecked || value) && content && <div className="action-cell__content">{content}</div>}
    </div>
  );
}

ActionCell.propTypes = {
  value: PropTypes.any,
  children: PropTypes.node,
  description: PropTypes.node,
  disabled: PropTypes.bool,
  iconName: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  suffixIconName: PropTypes.string,
  textLink: PropTypes.node,
  type: PropTypes.oneOf(['ingress', 'radio', 'round-cta', 'checkbox', 'textlink', 'toogle', 'select', 'plain', 'plainNoIcon']).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.node,
    }),
  ),
  className: PropTypes.string,
  loading: PropTypes.bool,
  content: PropTypes.node,
  hideContentWhenUnChecked: PropTypes.bool,
  disableRootClick: PropTypes.bool,
  isDanger: PropTypes.bool,
  isDescriptionError: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasBlueWarning: PropTypes.bool
};

export default ActionCell;
